import { OrcamentoIcon } from './OrcamentoUI';
import { useLiquidFeedback } from './LiquidUI';
import React, { useState, useEffect, useId } from 'react';
import api from '../services/api';
import './ObraRecursos.css';

// Ferramentas e RMM partilham um separador, independente das máquinas.
const CATEGORIAS = {
    equipamento: { grupo: 'Máquinas e equipamentos' },
    ferramenta: { grupo: 'Ferramentas', icone: '🔧', etiqueta: 'Ferramenta' },
    rmm: { grupo: 'Equipamentos de medição (RMM)', icone: '📏', etiqueta: 'RMM' },
};
const categoriaDe = (e) => (CATEGORIAS[e.categoria] ? e.categoria : 'equipamento');
const eFerramenta = e => ['ferramenta', 'rmm'].includes(categoriaDe(e));

const ObraRecursos = ({ obraId }) => {
    const fieldPrefix = useId();
    const liquidFeedback = useLiquidFeedback();
    const [activeTab, setActiveTab] = useState('trabalhadores');
    const [loading, setLoading] = useState(true);
    const [trabalhadores, setTrabalhadores] = useState([]);
    const [equipamentos, setEquipamentos] = useState([]);
    // Ferramentas/RMM registados que não estão na obra (não entram no documento de segurança)
    const [emFalta, setEmFalta] = useState([]);
    const [bulkBusy, setBulkBusy] = useState(null);
    const equipamentosObra = equipamentos.filter(e => !eFerramenta(e));
    const ferramentasObra = equipamentos.filter(eFerramenta);
    const recursosEquipamentos = activeTab === 'ferramentas' ? ferramentasObra : equipamentosObra;

    // Listas para seleção
    const [allTrabalhadores, setAllTrabalhadores] = useState([]);
    const [allEquipamentos, setAllEquipamentos] = useState([]);

    // Estado do formulário
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        loadRecursos();
    }, [obraId]);

    const loadRecursos = async () => {
        setLoading(true);
        try {
            const [trabRes, equipRes, faltaRes] = await Promise.all([
                api.get(`/obra-recursos/${obraId}/trabalhadores`),
                api.get(`/obra-recursos/${obraId}/equipamentos`),
                // O aviso é uma ajuda: se falhar, a lista carrega na mesma
                api.get(`/obra-recursos/${obraId}/equipamentos/em-falta`).catch(() => ({ data: { em_falta: [] } }))
            ]);
            setTrabalhadores(trabRes.data);
            setEquipamentos(equipRes.data);
            setEmFalta(faltaRes.data?.em_falta || []);
        } catch (error) {
            console.error('Erro ao carregar recursos:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadOptions = async () => {
        try {
            if (activeTab === 'trabalhadores' && allTrabalhadores.length === 0) {
                const res = await api.get('/trabalhadores');
                setAllTrabalhadores(res.data);
            } else if (activeTab !== 'trabalhadores' && allEquipamentos.length === 0) {
                const res = await api.get('/equipamentos');
                setAllEquipamentos(res.data);
            }
        } catch (error) {
            console.error('Erro ao carregar opções:', error);
        }
    };

    const handleAddClick = () => {
        loadOptions();
        setShowAddForm(true);
        setFormData({
            data_entrada: new Date().toISOString().split('T')[0]
        });
    };

    const handleSave = async () => {
        try {
            if (activeTab === 'trabalhadores') {
                await api.post(`/obra-recursos/${obraId}/trabalhadores`, {
                    ...formData
                });
            } else {
                await api.post(`/obra-recursos/${obraId}/equipamentos`, {
                    ...formData
                });
            }
            setShowAddForm(false);
            loadRecursos();
        } catch (error) {
            console.error('Erro ao salvar:', error);
            liquidFeedback.notify('Erro ao adicionar recurso. Verifique se todos os campos estão preenchidos.');
        }
    };

    const handleAddAll = async (grupo) => {
        if (bulkBusy) return;
        setBulkBusy(grupo);
        const mensagens = {
            trabalhadores: 'Adicionar todos os colaboradores ativos e aptos para obras? Os já associados mantêm-se sem alterações.',
            equipamentos: 'Adicionar todos os equipamentos ativos à obra? As ferramentas e os RMM são adicionados no respetivo separador. Os já associados mantêm-se sem alterações.',
            ferramentas: 'Adicionar todas as ferramentas e equipamentos de medição (RMM) ativos à obra? Os já associados mantêm-se sem alterações.'
        };
        try {
            if (!await liquidFeedback.confirm(mensagens[grupo])) return;
            const path = grupo === 'trabalhadores' ? 'trabalhadores/add-all'
                : grupo === 'ferramentas' ? 'equipamentos/add-ferramentas' : 'equipamentos/add-all';
            const result = await api.post(`/obra-recursos/${obraId}/${path}`, grupo === 'equipamentos' ? { grupo: 'equipamentos' } : {});
            setActiveTab(grupo);
            setShowAddForm(false);
            await loadRecursos();
            const count = result.data.adicionados ?? result.data.adicionadas;
            liquidFeedback.notify(count === 0 ? 'Todos os itens elegíveis já estavam associados à obra.'
                : Number.isInteger(count) ? `${count} recurso(s) adicionado(s) à obra.` : 'Recursos adicionados à obra.');
        } catch (error) {
            console.error('Erro ao adicionar recursos:', error);
            liquidFeedback.notify(error.response?.data?.error || 'Erro ao adicionar recursos. Tente novamente.');
        } finally {
            setBulkBusy(null);
        }
    };

    const handleDelete = async (id) => {
        if (!await liquidFeedback.confirm('Tem a certeza que deseja remover este recurso da obra?')) return;

        try {
            if (activeTab === 'trabalhadores') {
                await api.delete(`/obra-recursos/${obraId}/trabalhadores/${id}`);
            } else {
                await api.delete(`/obra-recursos/${obraId}/equipamentos/${id}`);
            }
            loadRecursos();
        } catch (error) {
            console.error('Erro ao remover:', error);
        }
    };

    const handleToggleAfeto = async (trabId, currentValue) => {
        try {
            await api.patch(`/obra-recursos/${obraId}/trabalhadores/${trabId}/afeto`, {
                afeto_obra: !currentValue
            });
            setTrabalhadores(prev =>
                prev.map(t => t.id === trabId ? { ...t, afeto_obra: !currentValue } : t)
            );
        } catch (error) {
            console.error('Erro ao atualizar afeto:', error);
        }
    };

    const renderTrabForm = () => (
        <div className="add-form">
            <h3>Adicionar Trabalhador</h3>
            <div className="form-grid">
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-0`}>Trabalhador:</label>
                    <select id={`${fieldPrefix}-0`}
                        value={formData.trabalhador_id || ''}
                        onChange={e => setFormData({ ...formData, trabalhador_id: e.target.value })}
                    >
                        <option value="">Selecione...</option>
                        {allTrabalhadores.filter(t => t.ativo !== false && !trabalhadores.some(x => String(x.trabalhador_id) === String(t.id))).map(t => (
                            <option key={t.id} value={t.id}>{t.nome}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-1`}>Função na Obra:</label>
                    <input id={`${fieldPrefix}-1`}
                        type="text"
                        value={formData.funcao_na_obra || ''}
                        onChange={e => setFormData({ ...formData, funcao_na_obra: e.target.value })}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-2`}>Data Entrada:</label>
                    <input id={`${fieldPrefix}-2`}
                        type="date"
                        value={formData.data_entrada || ''}
                        onChange={e => setFormData({ ...formData, data_entrada: e.target.value })}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-3`}>Data Saída (Prevista):</label>
                    <input id={`${fieldPrefix}-3`}
                        type="date"
                        value={formData.data_saida || ''}
                        onChange={e => setFormData({ ...formData, data_saida: e.target.value })}
                    />
                </div>
            </div>
            <div className="form-actions">
                <button className="btn-cancel" onClick={() => setShowAddForm(false)}>Cancelar</button>
                <button className="btn-save" onClick={handleSave}><OrcamentoIcon name="plus" fallback="+" /> Adicionar</button>
            </div>
        </div>
    );

    const idsNaObra = new Set(equipamentos.map(x => String(x.equipamento_id)));

    const renderEquipForm = () => (
        <div className="add-form">
            <h3>{activeTab === 'ferramentas' ? 'Adicionar Ferramenta' : 'Adicionar Equipamento'}</h3>
            <div className="form-grid">
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-4`}>{activeTab === 'ferramentas' ? 'Ferramenta:' : 'Equipamento:'}</label>
                    <select id={`${fieldPrefix}-4`}
                        value={formData.equipamento_id || ''}
                        onChange={e => setFormData({ ...formData, equipamento_id: e.target.value })}
                    >
                        <option value="">Selecione...</option>
                        {/* Por categoria, sem os que já estão na obra */}
                        {Object.entries(CATEGORIAS).filter(([cat]) => activeTab === 'ferramentas' ? cat !== 'equipamento' : cat === 'equipamento').map(([cat, c]) => {
                            const opcoes = allEquipamentos.filter(e => e.ativo !== false && categoriaDe(e) === cat && !idsNaObra.has(String(e.id)));
                            if (!opcoes.length) return null;
                            return (
                                <optgroup key={cat} label={c.grupo}>
                                    {opcoes.map(e => (
                                        <option key={e.id} value={e.id}>
                                            {e.nome} - {e.matricula || e.n_serie || (cat === 'equipamento' ? 'S/ Matrícula' : 'S/ nº de série')}
                                        </option>
                                    ))}
                                </optgroup>
                            );
                        })}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-5`}>Data Entrada:</label>
                    <input id={`${fieldPrefix}-5`}
                        type="date"
                        value={formData.data_entrada || ''}
                        onChange={e => setFormData({ ...formData, data_entrada: e.target.value })}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-6`}>Data Saída:</label>
                    <input id={`${fieldPrefix}-6`}
                        type="date"
                        value={formData.data_saida || ''}
                        onChange={e => setFormData({ ...formData, data_saida: e.target.value })}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-7`}>Responsável:</label>
                    <input id={`${fieldPrefix}-7`}
                        type="text"
                        value={formData.responsavel || ''}
                        onChange={e => setFormData({ ...formData, responsavel: e.target.value })}
                    />
                </div>
            </div>

            <h4>Dados DL50 (Opcional)</h4>
            <div className="form-grid">
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-8`}>Número DL50:</label>
                    <input id={`${fieldPrefix}-8`}
                        type="text"
                        value={formData.dl50_numero || ''}
                        onChange={e => setFormData({ ...formData, dl50_numero: e.target.value })}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-9`}>Data Verificação:</label>
                    <input id={`${fieldPrefix}-9`}
                        type="date"
                        value={formData.dl50_data_verificacao || ''}
                        onChange={e => setFormData({ ...formData, dl50_data_verificacao: e.target.value })}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-10`}>Próxima Verificação:</label>
                    <input id={`${fieldPrefix}-10`}
                        type="date"
                        value={formData.dl50_proxima_verificacao || ''}
                        onChange={e => setFormData({ ...formData, dl50_proxima_verificacao: e.target.value })}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${fieldPrefix}-11`}>Resultado:</label>
                    <select id={`${fieldPrefix}-11`}
                        value={formData.dl50_resultado || ''}
                        onChange={e => setFormData({ ...formData, dl50_resultado: e.target.value })}
                    >
                        <option value="">Selecione...</option>
                        <option value="Aprovado">Aprovado</option>
                        <option value="Reprovado">Reprovado</option>
                        <option value="Condicionado">Condicionado</option>
                    </select>
                </div>
            </div>

            <div className="form-actions">
                <button className="btn-cancel" onClick={() => setShowAddForm(false)}>Cancelar</button>
                <button className="btn-save" onClick={handleSave}><OrcamentoIcon name="plus" fallback="+" /> Adicionar</button>
            </div>
        </div>
    );

    return (
        <div className="obra-recursos-container" id="obra-recursos">
            <div className="recursos-tabs">
                {[
                    ['trabalhadores', 'Colaboradores', 'users', trabalhadores.length],
                    ['equipamentos', 'Equipamentos', 'equipment', equipamentosObra.length],
                    ['ferramentas', 'Ferramentas', 'maintenance', ferramentasObra.length]
                ].map(([key, label, icon, count]) => (
                    <button key={key} className={`tab-btn ${activeTab === key ? 'active' : ''}`}
                        aria-pressed={activeTab === key} disabled={!!bulkBusy}
                        onClick={() => { setActiveTab(key); setShowAddForm(false); }}>
                        <OrcamentoIcon name={icon} fallback={key === 'trabalhadores' ? '👷' : key === 'equipamentos' ? '🚜' : '🔧'} /> {label} ({count})
                    </button>
                ))}
            </div>
            {!showAddForm && <div className="recursos-bulk-actions" aria-label="Adicionar recursos em lote">
                {[
                    ['trabalhadores', 'Adicionar todos os colaboradores', 'users'],
                    ['equipamentos', 'Adicionar todos os equipamentos', 'equipment'],
                    ['ferramentas', 'Adicionar todas as ferramentas', 'maintenance']
                ].map(([key, label, icon]) => (
                    <button key={key} className="btn-add" onClick={() => handleAddAll(key)} disabled={loading || !!bulkBusy}>
                        <OrcamentoIcon name={bulkBusy === key ? 'clock' : icon} fallback="+" /> {bulkBusy === key ? 'A adicionar…' : label}
                    </button>
                ))}
            </div>}
            {activeTab === 'ferramentas' && <p className="recursos-section-hint">Inclui ferramentas e equipamentos de medição (RMM).</p>}
            {activeTab === 'ferramentas' && emFalta.length > 0 && (
                <div className="recursos-aviso" role="status">
                    <div className="recursos-aviso-texto">
                        <strong>
                            <OrcamentoIcon name="maintenance" fallback="🔧" /> {emFalta.length === 1
                                ? '1 ferramenta registada não está nesta obra'
                                : `${emFalta.length} ferramentas registadas não estão nesta obra`}
                        </strong>
                        {' '}— não aparecem no documento de segurança.
                        <details>
                            <summary>Ver quais</summary>
                            <ul>
                                {emFalta.map(f => (
                                    <li key={f.id}>
                                        <OrcamentoIcon name={f.categoria === 'rmm' ? 'ruler' : 'maintenance'} fallback={CATEGORIAS[f.categoria]?.icone} /> {f.nome}{f.n_serie ? ` (nº de série ${f.n_serie})` : ''}
                                        {f.registado_por_nome && <small> — registada por {f.registado_por_nome}</small>}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    </div>
                    <button className="btn-add" onClick={() => handleAddAll('ferramentas')} disabled={!!bulkBusy}>
                        <OrcamentoIcon name={bulkBusy === 'ferramentas' ? 'clock' : 'plus'} fallback="+" /> {bulkBusy === 'ferramentas' ? 'A adicionar…' : emFalta.length === 1 ? 'Adicionar à obra' : `Adicionar as ${emFalta.length} à obra`}
                    </button>
                </div>
            )}

            <div className="recursos-content">
                {!showAddForm && (
                    <div className="recursos-actions" style={{ gap: '10px' }}>
                        <button className="btn-add" onClick={handleAddClick}>
                            <OrcamentoIcon name="plus" fallback="+" /> Adicionar {activeTab === 'trabalhadores' ? 'Colaborador' : activeTab === 'ferramentas' ? 'Ferramenta' : 'Equipamento'}
                        </button>
                    </div>
                )}

                {showAddForm && (activeTab === 'trabalhadores' ? renderTrabForm() : renderEquipForm())}

                {!showAddForm && activeTab === 'trabalhadores' && (
                    trabalhadores.length > 0 ? (
                        <div className="recursos-table-wrap"><table className="recursos-table">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Função</th>
                                    <th>Entrada</th>
                                    <th>Saída</th>
                                    <th style={{ textAlign: 'center', width: '60px' }}>Afeto</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trabalhadores.map(t => (
                                    <tr key={t.id}>
                                        <td>
                                            <strong>{t.nome}</strong><br />
                                            <small className="text-gray-500">{t.empresa}</small>
                                        </td>
                                        <td>{t.funcao_na_obra || '-'}</td>
                                        <td>{new Date(t.data_entrada).toLocaleDateString()}</td>
                                        <td>{t.data_saida ? new Date(t.data_saida).toLocaleDateString() : '-'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={t.afeto_obra !== false}
                                                onChange={() => handleToggleAfeto(t.id, t.afeto_obra !== false)}
                                                title={t.afeto_obra !== false ? 'Afeto à obra (aparece no PDF)' : 'Não afeto (não aparece no PDF)'}
                                                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                            />
                                        </td>
                                        <td className="actions-cell">
                                            <button className="btn-icon btn-delete" onClick={() => handleDelete(t.id)} title="Remover"><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table></div>
                    ) : (
                        <div className="empty-state">
                            <p>Nenhum trabalhador associado a esta obra.</p>
                        </div>
                    )
                )}

                {!showAddForm && activeTab !== 'trabalhadores' && (
                    recursosEquipamentos.length > 0 ? (
                        <div className="recursos-table-wrap"><table className="recursos-table">
                            <thead>
                                <tr>
                                    <th>{activeTab === 'ferramentas' ? 'Ferramenta / RMM' : 'Equipamento'}</th>
                                    <th>Matrícula / Nº série</th>
                                    <th>Entrada</th>
                                    <th>DL50</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recursosEquipamentos.map(e => (
                                    <tr key={e.id}>
                                        <td>
                                            <strong>{e.nome}</strong>
                                            {CATEGORIAS[categoriaDe(e)].etiqueta && (
                                                <span className="recursos-cat">
                                                    <OrcamentoIcon name={categoriaDe(e) === 'rmm' ? 'ruler' : 'maintenance'} fallback={CATEGORIAS[categoriaDe(e)].icone} /> {CATEGORIAS[categoriaDe(e)].etiqueta}
                                                </span>
                                            )}
                                            <br />
                                            <small>{e.tipo}</small>
                                        </td>
                                        <td>{e.matricula || e.n_serie || '-'}</td>
                                        <td>{new Date(e.data_entrada).toLocaleDateString()}</td>
                                        <td>
                                            {e.dl50_numero ? (
                                                <span title={`Próxima verificação: ${new Date(e.dl50_proxima_verificacao).toLocaleDateString()}`}>
                                                    <OrcamentoIcon name="check" fallback="✅" /> {e.dl50_numero}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="actions-cell">
                                            <button className="btn-icon btn-delete" onClick={() => handleDelete(e.id)} title="Remover"><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table></div>
                    ) : (
                        <div className="empty-state">
                            <p>{activeTab === 'ferramentas' ? 'Nenhuma ferramenta associada a esta obra.' : 'Nenhum equipamento associado a esta obra.'}</p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default ObraRecursos;
